import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, ArrowLeft, CheckCircle2, ShieldCheck, Loader2, FileText } from "lucide-react";

const PRO_OPTIONS = ["BMI", "ASCAP", "SESAC", "GMR", "UNSURE"];
const CATALOG_SIZES = ["1-10", "11-25", "26-50", "51-100", "100+"];
const LLC_STATES = [
  { v: "yes", l: "Yes, I have an LLC" },
  { v: "no", l: "No, and I don't need one right now" },
  { v: "needs_setup", l: "No, but I want E-Quantum to set one up" },
];

const STEPS = [
  { id: "identity", title: "Legal Identity", subtitle: "Who we're registering." },
  { id: "contact", title: "Contact & Address", subtitle: "For every service that requires a mailing address." },
  { id: "business", title: "Business Entity", subtitle: "Publisher entity & tax setup." },
  { id: "music", title: "Your Music", subtitle: "Where you are right now — so we can meet you there." },
  { id: "services", title: "Choose Services", subtitle: "Pick which platforms we'll register you with." },
  { id: "banking", title: "Direct Deposit", subtitle: "Where the royalties should land." },
  { id: "sign", title: "Consent & Sign", subtitle: "Authorize us to file on your behalf." },
];

const emptyForm = {
  legal_first_name: "", legal_last_name: "", stage_name: "", dob: "",
  ssn_or_ein: "", phone: "",
  address_line1: "", address_line2: "", city: "", state_region: "", postal_code: "", country: "US",
  has_llc: "no", llc_name: "", ein: "", publisher_entity_name: "",
  primary_pro_preference: "BMI", prior_pro_membership: "no",
  catalog_size: "1-10", distributor: "",
  target_services: [],
  bank_name: "", bank_routing_last4: "", bank_account_last4: "",
  consent_to_file: false, signature: "",
};

export default function StructureOnboarding() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [complete, setComplete] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: cat }, { data: mine }] = await Promise.all([
          api.get("/structure/services"),
          api.get("/structure/my-intake"),
        ]);
        setServices(cat);
        if (mine.exists) {
          setForm({ ...emptyForm, ...mine, consent_to_file: false, signature: "" });
        }
      } catch (e) {
        console.error("Onboarding load failed", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const patch = (updates) => setForm((f) => ({ ...f, ...updates }));

  const toggleService = (key) => {
    setForm((f) => ({
      ...f,
      target_services: f.target_services.includes(key)
        ? f.target_services.filter((k) => k !== key)
        : [...f.target_services, key],
    }));
  };

  const canAdvance = () => {
    const s = STEPS[step].id;
    if (s === "identity") return form.legal_first_name && form.legal_last_name && form.dob && form.ssn_or_ein;
    if (s === "contact") return form.phone && form.address_line1 && form.city && form.state_region && form.postal_code;
    if (s === "business") return true;
    if (s === "music") return form.primary_pro_preference && form.catalog_size;
    if (s === "services") return form.target_services.length > 0;
    if (s === "banking") return true;
    if (s === "sign") return form.consent_to_file && form.signature.length > 2;
    return true;
  };

  const submit = async () => {
    if (!form.consent_to_file || !form.signature) {
      toast.error("Please consent and sign to complete the intake.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/structure/intake", form);
      setComplete(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <Loader2 className="text-[var(--primary)] animate-spin" size={32} />
      </div>
    );
  }

  if (complete) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="max-w-xl w-full text-center"
          data-testid="structure-complete"
        >
          <CheckCircle2 size={56} className="text-[var(--primary)] mx-auto mb-8" strokeWidth={1.3} />
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--primary)] mb-4">
            / intake received
          </div>
          <h1 className="font-heading font-black text-4xl md:text-6xl leading-[0.95] tracking-tighter mb-6">
            You&rsquo;re set,{" "}
            <span className="text-[var(--primary)]">{form.legal_first_name}</span>.
          </h1>
          <p className="text-zinc-400 text-base md:text-lg leading-relaxed mb-10">
            Our team begins filing on your behalf immediately. You&rsquo;ll see status
            updates for each registration in your dashboard as they move through
            <em> Submitted → Approved → Active</em>. Most take 2–4 weeks.
          </p>
          <button
            type="button"
            onClick={() => nav("/dashboard")}
            className="btn-primary"
            data-testid="structure-goto-dashboard"
          >
            Open my dashboard <ArrowRight size={16} />
          </button>
        </motion.div>
      </div>
    );
  }

  const current = STEPS[step];
  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-[#050505] py-16 md:py-24 px-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--primary)] mb-3">
            // Structure Onboarding
          </div>
          <h1 className="font-heading font-black text-3xl md:text-5xl tracking-tighter leading-[0.95] mb-4">
            One intake.<br />
            <span className="text-[var(--primary)]">Every registration filed.</span>
          </h1>
          <p className="text-zinc-400 text-sm md:text-base leading-relaxed max-w-xl">
            Answer this once. We&rsquo;ll register you with every service you select and update your dashboard as each one goes live.
          </p>
        </div>

        {/* Progress */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500">
              Step {step + 1} / {STEPS.length} · {current.title}
            </div>
            <div className="font-mono text-[10px] tracking-widest uppercase text-[var(--primary)]">
              {Math.round(progress)}%
            </div>
          </div>
          <div className="h-1 w-full bg-white/10 overflow-hidden">
            <div
              className="h-full bg-[var(--primary)] transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Step body */}
        <motion.div
          key={current.id}
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="border border-white/10 bg-[#0a0a0a] p-8 md:p-12"
          data-testid={`onboarding-step-${current.id}`}
        >
          <h2 className="font-heading font-black text-2xl md:text-3xl mb-2 tracking-tight">
            {current.title}
          </h2>
          <p className="text-zinc-500 text-sm mb-8">{current.subtitle}</p>

          {current.id === "identity" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Legal First Name" value={form.legal_first_name} onChange={(v) => patch({ legal_first_name: v })} testid="fld-legal-first" />
              <Field label="Legal Last Name" value={form.legal_last_name} onChange={(v) => patch({ legal_last_name: v })} testid="fld-legal-last" />
              <Field label="Stage / Artist Name" value={form.stage_name} onChange={(v) => patch({ stage_name: v })} placeholder="Optional" testid="fld-stage" />
              <Field label="Date of Birth" type="date" value={form.dob} onChange={(v) => patch({ dob: v })} testid="fld-dob" />
              <Field label="SSN or EIN (last 4 is fine)" value={form.ssn_or_ein} onChange={(v) => patch({ ssn_or_ein: v })} placeholder="•••• or full" testid="fld-ssn" />
            </div>
          )}

          {current.id === "contact" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Phone" value={form.phone} onChange={(v) => patch({ phone: v })} testid="fld-phone" />
              <Field label="Country" value={form.country} onChange={(v) => patch({ country: v })} testid="fld-country" />
              <div className="md:col-span-2">
                <Field label="Address Line 1" value={form.address_line1} onChange={(v) => patch({ address_line1: v })} testid="fld-addr1" />
              </div>
              <div className="md:col-span-2">
                <Field label="Address Line 2 (optional)" value={form.address_line2} onChange={(v) => patch({ address_line2: v })} testid="fld-addr2" />
              </div>
              <Field label="City" value={form.city} onChange={(v) => patch({ city: v })} testid="fld-city" />
              <Field label="State / Region" value={form.state_region} onChange={(v) => patch({ state_region: v })} testid="fld-state" />
              <Field label="Postal Code" value={form.postal_code} onChange={(v) => patch({ postal_code: v })} testid="fld-zip" />
            </div>
          )}

          {current.id === "business" && (
            <div className="space-y-5">
              <div>
                <Label>Do you have an LLC?</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {LLC_STATES.map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => patch({ has_llc: o.v })}
                      className={`border p-4 text-left text-sm transition-colors ${form.has_llc === o.v ? "border-[var(--primary)] bg-[var(--primary)]/5 text-white" : "border-white/10 text-zinc-400 hover:border-white/30"}`}
                      data-testid={`llc-${o.v}`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>
              {form.has_llc === "yes" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="LLC Name" value={form.llc_name} onChange={(v) => patch({ llc_name: v })} testid="fld-llc-name" />
                  <Field label="EIN" value={form.ein} onChange={(v) => patch({ ein: v })} testid="fld-ein" />
                </div>
              )}
              <Field
                label="Publisher Entity Name (leave blank and we'll create one)"
                value={form.publisher_entity_name}
                onChange={(v) => patch({ publisher_entity_name: v })}
                testid="fld-pub-entity"
              />
            </div>
          )}

          {current.id === "music" && (
            <div className="space-y-6">
              <div>
                <Label>Preferred PRO</Label>
                <div className="flex flex-wrap gap-3">
                  {PRO_OPTIONS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => patch({ primary_pro_preference: p })}
                      className={`border px-5 py-2.5 font-mono text-xs tracking-widest uppercase transition-colors ${form.primary_pro_preference === p ? "border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--primary)]" : "border-white/10 text-zinc-400 hover:border-white/30"}`}
                      data-testid={`pro-${p.toLowerCase()}`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Have you been a member of any PRO before?</Label>
                <div className="flex gap-3">
                  {["yes", "no"].map((v) => (
                    <button key={v} type="button" onClick={() => patch({ prior_pro_membership: v })}
                      className={`border px-5 py-2.5 font-mono text-xs tracking-widest uppercase ${form.prior_pro_membership === v ? "border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--primary)]" : "border-white/10 text-zinc-400 hover:border-white/30"}`}
                      data-testid={`prior-${v}`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>How many songs are in your catalog?</Label>
                <div className="flex flex-wrap gap-3">
                  {CATALOG_SIZES.map((c) => (
                    <button key={c} type="button" onClick={() => patch({ catalog_size: c })}
                      className={`border px-5 py-2.5 font-mono text-xs tracking-widest uppercase ${form.catalog_size === c ? "border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--primary)]" : "border-white/10 text-zinc-400 hover:border-white/30"}`}
                      data-testid={`cat-${c}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <Field label="Current Distributor (optional)" value={form.distributor} onChange={(v) => patch({ distributor: v })} placeholder="DistroKid, TuneCore, etc." testid="fld-distributor" />
            </div>
          )}

          {current.id === "services" && (
            <div>
              <p className="text-zinc-400 text-sm mb-6">
                Select every service you want us to register you with. We&rsquo;ll pull data from this intake to file each one on your behalf.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="services-picker">
                {services.map((s) => {
                  const on = form.target_services.includes(s.key);
                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => toggleService(s.key)}
                      className={`border p-4 text-left flex items-start gap-3 transition-colors ${on ? "border-[var(--primary)] bg-[var(--primary)]/5" : "border-white/10 hover:border-white/30"}`}
                      data-testid={`svc-${s.key}`}
                    >
                      <div className={`w-5 h-5 border-2 flex-shrink-0 flex items-center justify-center mt-0.5 transition-colors ${on ? "border-[var(--primary)] bg-[var(--primary)]" : "border-white/30"}`}>
                        {on && <CheckCircle2 size={14} className="text-black" strokeWidth={2.5} />}
                      </div>
                      <div>
                        <div className="text-white font-bold text-sm mb-1">{s.name}</div>
                        <div className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase">{s.category}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="mt-6 font-mono text-[10px] tracking-widest uppercase text-zinc-600">
                {form.target_services.length} services selected
              </p>
            </div>
          )}

          {current.id === "banking" && (
            <div className="space-y-5">
              <div className="border border-[var(--primary)]/30 bg-[var(--primary)]/5 p-5">
                <div className="flex items-start gap-3">
                  <ShieldCheck size={20} className="text-[var(--primary)] flex-shrink-0 mt-0.5" />
                  <p className="text-zinc-300 text-sm leading-relaxed">
                    We only need the <strong>last 4 digits</strong> for reference. Full banking info is collected by each individual service through their secure portals during registration.
                  </p>
                </div>
              </div>
              <Field label="Bank Name" value={form.bank_name} onChange={(v) => patch({ bank_name: v })} placeholder="e.g. Chase, Bank of America" testid="fld-bank" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Routing (last 4)" value={form.bank_routing_last4} onChange={(v) => patch({ bank_routing_last4: v.slice(0, 4) })} placeholder="••••" testid="fld-routing" />
                <Field label="Account (last 4)" value={form.bank_account_last4} onChange={(v) => patch({ bank_account_last4: v.slice(0, 4) })} placeholder="••••" testid="fld-account" />
              </div>
            </div>
          )}

          {current.id === "sign" && (
            <div className="space-y-6">
              <div className="border border-white/10 bg-black/40 p-6 text-sm text-zinc-300 leading-relaxed">
                <FileText size={18} className="text-[var(--primary)] mb-3" />
                By signing below, you authorize <strong>E-Quantum Consults, LLC</strong> to:
                <ul className="mt-3 space-y-2 list-disc pl-5">
                  <li>File your writer &amp; publisher registrations with your chosen PRO and other selected services.</li>
                  <li>Act as your authorized agent for royalty collection and metadata correction across the selected platforms.</li>
                  <li>Contact you at the email &amp; phone provided for status updates on each registration.</li>
                </ul>
                <p className="mt-3 text-zinc-500 text-xs">
                  You may revoke this authorization in writing at any time. This does not transfer ownership of your masters, publishing, or copyrights — you retain 100%.
                </p>
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.consent_to_file}
                  onChange={(e) => patch({ consent_to_file: e.target.checked })}
                  className="mt-1 w-4 h-4 accent-[var(--primary)]"
                  data-testid="consent-checkbox"
                />
                <span className="text-sm text-zinc-200">I authorize E-Quantum to file on my behalf as described above.</span>
              </label>
              <Field label="Type your full legal name to sign" value={form.signature} onChange={(v) => patch({ signature: v })} testid="signature-input" />
              <p className="font-mono text-[10px] tracking-widest uppercase text-zinc-600">
                Digital signature · legally binding
              </p>
            </div>
          )}
        </motion.div>

        {/* Nav */}
        <div className="mt-8 flex items-center justify-between sticky bottom-4 md:static bg-[#050505]/90 backdrop-blur-sm p-3 md:p-0 border md:border-0 border-white/10 mobile-safe-bottom md:mobile-safe-bottom-none z-10 -mx-2 md:mx-0">
          <button
            type="button"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            className="btn-ghost disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="btn-back"
          >
            <ArrowLeft size={14} /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={() => canAdvance() ? setStep((s) => s + 1) : toast.error("Please complete this step before continuing.")}
              className="btn-primary"
              data-testid="btn-next"
            >
              Continue <ArrowRight size={14} />
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={submitting || !canAdvance()}
              className="btn-primary disabled:opacity-50"
              data-testid="btn-submit-intake"
            >
              {submitting ? (
                <><Loader2 size={16} className="animate-spin" /> Submitting…</>
              ) : (
                <>Sign &amp; Submit <ArrowRight size={14} /></>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder = "", testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-transparent border-b border-white/20 focus:border-[var(--primary)] outline-none py-2.5 text-white placeholder-zinc-600 text-base transition-colors"
      />
    </div>
  );
}

function Label({ children }) {
  return (
    <label className="block font-mono text-[10px] tracking-widest uppercase text-zinc-500 mb-2">
      {children}
    </label>
  );
}
